import { useEffect, useState, type FormEvent } from 'react';
import type { CosmosSettings, CosmosTestResult } from '@ia/shared';
import { api, errorMessage } from '../api';
import { Alert, Field, Spinner, When } from '../components/ui';

type KeyMode = 'keep' | 'replace' | 'remove';

/** DataHub Cosmos: where the Insurance app's PAT per workspace lives (what "Create contacts" and the contact check sign in with). */
export function CosmosSettingsPage() {
  const [settings, setSettings] = useState<CosmosSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [database, setDatabase] = useState('');
  const [container, setContainer] = useState('');
  const [keyMode, setKeyMode] = useState<KeyMode>('keep');
  const [connectionString, setConnectionString] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<CosmosTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const hydrate = (s: CosmosSettings) => {
    setSettings(s);
    setDatabase(s.database);
    setContainer(s.container);
    setKeyMode(s.source === 'settings' ? 'keep' : 'replace');
    setConnectionString('');
  };

  useEffect(() => {
    api.cosmosSettings().then(hydrate, (e) => setLoadError(errorMessage(e)));
  }, []);

  const dirty =
    !!settings &&
    (database.trim() !== settings.database ||
      container.trim() !== settings.container ||
      (keyMode === 'replace' && connectionString.length > 0) ||
      keyMode === 'remove');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    if (!database.trim() || !container.trim()) return setSaveError('Enter the database and container names.');
    const body: Parameters<typeof api.updateCosmosSettings>[0] = { database: database.trim(), container: container.trim() };
    if (keyMode === 'replace' && connectionString) {
      if (!/AccountEndpoint=/i.test(connectionString)) return setSaveError('That does not look like a Cosmos connection string (no AccountEndpoint=).');
      body.connectionString = connectionString.trim();
    } else if (keyMode === 'remove') {
      body.connectionString = null;
    }
    setSaving(true);
    try {
      hydrate(await api.updateCosmosSettings(body));
      setSaved(true);
      setTest(null);
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setTestError(null);
    try {
      setTest(await api.testCosmos());
    } catch (e) {
      setTestError(errorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  if (loadError)
    return (
      <div className="page">
        <h1>DataHub (Cosmos)</h1>
        <Alert>{loadError}</Alert>
      </div>
    );
  if (!settings)
    return (
      <div className="page">
        <Spinner />
      </div>
    );

  const sourceText =
    settings.source === 'settings'
      ? `Using the connection string saved here (${settings.accountEndpoint ?? '?'}).`
      : settings.source === 'env'
        ? `Using the server's COSMOS_CONNECTION_STRING environment variable (${settings.accountEndpoint ?? '?'}). Saving one here overrides it.`
        : 'No connection string anywhere: contacts are checked with the advisor session or destination PAT instead, and "Create contacts" is disabled.';

  return (
    <div className="page narrow">
      <div className="page-head">
        <h1>Settings · DataHub (Cosmos)</h1>
      </div>
      <p className="muted">
        The DataHub InsurancesWorker signs in to Maximizer with the Insurance app's token stored per workspace in this Cosmos account
        (the <code>application-install</code> container). The admin reads the same record to check which clients exist in the address
        book and to create the missing ones, so the preview shows what the worker will see.
      </p>
      <Alert kind={settings.source === 'none' ? 'warn' : 'info'}>{sourceText}</Alert>

      <form className="card form-card" onSubmit={submit}>
        <Field label="Database" hint="The worker's CosmosDatabaseConfiguration:DatabaseId.">
          {(id) => <input id={id} value={database} onChange={(e) => setDatabase(e.target.value)} maxLength={100} spellCheck={false} required />}
        </Field>
        <Field label="Container" hint="Holds one application-install record per workspace and app.">
          {(id) => <input id={id} value={container} onChange={(e) => setContainer(e.target.value)} maxLength={100} spellCheck={false} required />}
        </Field>

        <div className="field">
          <span className="label">Connection string</span>
          {settings.source === 'settings' && keyMode === 'keep' && (
            <div className="key-row">
              <code className="key-hint">AccountEndpoint={settings.accountEndpoint ?? '?'}; AccountKey=…</code>
              <button type="button" className="btn btn-sm" onClick={() => setKeyMode('replace')}>
                Replace
              </button>
              <button type="button" className="btn btn-sm btn-danger-ghost" onClick={() => setKeyMode('remove')}>
                Remove
              </button>
            </div>
          )}
          {keyMode === 'remove' && (
            <div className="key-row">
              <span className="text-failed">The saved connection string will be removed when you save; the server environment is used instead, if set.</span>
              <button type="button" className="btn btn-sm" onClick={() => setKeyMode('keep')}>
                Undo
              </button>
            </div>
          )}
          {keyMode === 'replace' && (
            <div className="key-row">
              <input
                type="password"
                aria-label="Cosmos account connection string"
                placeholder="AccountEndpoint=https://….documents.azure.com:443/;AccountKey=…;"
                value={connectionString}
                onChange={(e) => setConnectionString(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="grow"
              />
              {settings.source === 'settings' && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setConnectionString('');
                    setKeyMode('keep');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
          <div className="hint">Write-only: stored encrypted on the server and never shown again. Leave empty to keep the current one.</div>
        </div>

        {saveError && <Alert>{saveError}</Alert>}
        {saved && !dirty && <Alert kind="ok">Settings saved.</Alert>}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void runTest()}
            disabled={testing || !settings.hasConnectionString || dirty}
            title={dirty ? 'Save your changes first' : !settings.hasConnectionString ? 'Add a connection string first' : undefined}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <span className="spacer" />
          <span className="muted small">
            Last updated {settings.updatedAt ? <When iso={settings.updatedAt} inline /> : 'never'}
            {settings.updatedBy && <> by {settings.updatedBy}</>}
          </span>
        </div>

        {testError && <Alert>{testError}</Alert>}
        {test &&
          (test.ok ? (
            <Alert kind="ok">
              Connection OK — <code>{test.accountEndpoint}</code>, database <code>{test.database}</code>, container <code>{test.container}</code> found.
            </Alert>
          ) : (
            <Alert>
              Connection failed{test.accountEndpoint ? <> for <code>{test.accountEndpoint}</code></> : null}: {test.error ?? 'unknown error'}
            </Alert>
          ))}
      </form>
    </div>
  );
}
